import { zeroErrorMessage } from "@/lib/zero-error";
import { useQuery, useZero } from "@rocicorp/zero/react";
import { appMutators, appQueries } from "@production-hub/shared";
import { toast } from "sonner";
import type {
  Tables,
  TablesInsert,
  TablesUpdate,
} from "@/integrations/neon/types";
import { generateUUID } from "@/lib/utils";
import {
  reportServerMutationFailure,
  toLegacyTimestamp,
  usePendingMutation,
  type MutationOptions,
  type ZeroMutateClient,
} from "./useZeroMutationUtils";

export type Profile = Tables<"profiles">;
export type ProfileInsert = TablesInsert<"profiles">;
export type ProfileUpdate = TablesUpdate<"profiles">;

type ZeroProfile = {
  id: string;
  name: string;
  videoCodec?: string | null;
  videoWidth?: number | null;
  videoHeight?: number | null;
  videoBitrate?: number | null;
  videoFramerate?: string | null;
  audioCodec?: string | null;
  audioBitrate?: number | null;
  audioSampleRate?: number | null;
  config?: Profile["config"];
  createdAt?: number | string | null;
  updatedAt?: number | string | null;
};

function toLegacyProfile(row: ZeroProfile): Profile {
  return {
    id: row.id,
    name: row.name,
    video_codec: row.videoCodec ?? null,
    video_width: row.videoWidth ?? null,
    video_height: row.videoHeight ?? null,
    video_bitrate: row.videoBitrate ?? null,
    video_framerate: row.videoFramerate ?? null,
    audio_codec: row.audioCodec ?? null,
    audio_bitrate: row.audioBitrate ?? null,
    audio_sample_rate: row.audioSampleRate ?? null,
    config: row.config ?? {},
    created_at: toLegacyTimestamp(row.createdAt),
    updated_at: toLegacyTimestamp(row.updatedAt),
  };
}

function toZeroProfileInput(profile: ProfileInsert) {
  return {
    id: profile.id ?? generateUUID(),
    name: profile.name,
    videoCodec: profile.video_codec ?? null,
    videoWidth: profile.video_width ?? null,
    videoHeight: profile.video_height ?? null,
    videoBitrate: profile.video_bitrate ?? null,
    videoFramerate: profile.video_framerate ?? null,
    audioCodec: profile.audio_codec ?? null,
    audioBitrate: profile.audio_bitrate ?? null,
    audioSampleRate: profile.audio_sample_rate ?? null,
    config: profile.config ?? {},
  };
}

function toZeroProfileUpdate(id: string, profile: ProfileUpdate) {
  return {
    id,
    ...(profile.name !== undefined ? { name: profile.name } : {}),
    ...(profile.video_codec !== undefined
      ? { videoCodec: profile.video_codec }
      : {}),
    ...(profile.video_width !== undefined
      ? { videoWidth: profile.video_width }
      : {}),
    ...(profile.video_height !== undefined
      ? { videoHeight: profile.video_height }
      : {}),
    ...(profile.video_bitrate !== undefined
      ? { videoBitrate: profile.video_bitrate }
      : {}),
    ...(profile.video_framerate !== undefined
      ? { videoFramerate: profile.video_framerate }
      : {}),
    ...(profile.audio_codec !== undefined
      ? { audioCodec: profile.audio_codec }
      : {}),
    ...(profile.audio_bitrate !== undefined
      ? { audioBitrate: profile.audio_bitrate }
      : {}),
    ...(profile.audio_sample_rate !== undefined
      ? { audioSampleRate: profile.audio_sample_rate }
      : {}),
    ...(profile.config !== undefined ? { config: profile.config } : {}),
  };
}

export const useProfiles = () => {
  const [data, details] = useQuery(appQueries.profiles.list());
  const profiles = ((data ?? []) as ZeroProfile[]).map(toLegacyProfile);
  return {
    data: profiles,
    isLoading: details.type !== "complete" && profiles.length === 0,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
};

export const useSingleProfile = (profileId: string | undefined) => {
  const query = profileId
    ? appQueries.profiles.byID({ id: profileId })
    : undefined;
  const [data, details] = useQuery(query, { enabled: !!profileId });
  return {
    data: data ? toLegacyProfile(data as ZeroProfile) : null,
    isLoading: !!profileId && details.type !== "complete" && !data,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
};

export const useCreateProfile = () => {
  const zero = useZero() as ZeroMutateClient;
  const { isPending, runPending } = usePendingMutation();
  const run = (profile: ProfileInsert) =>
    runPending(async () => {
      const input = toZeroProfileInput(profile);
      const mutation = zero.mutate(appMutators.profiles.create(input));
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Profile create was rejected",
      );
      return toLegacyProfile(input);
    });
  return {
    mutate: (profile: ProfileInsert, options?: MutationOptions<Profile>) => {
      void run(profile)
        .then((data) => {
          toast.success("Profile created");
          options?.onSuccess?.(data);
        })
        .catch((err) =>
          toast.error("Failed to create profile: " + err.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
};

export const useUpdateProfile = () => {
  const zero = useZero() as ZeroMutateClient;
  const { isPending, runPending } = usePendingMutation();
  const run = ({ id, profile }: { id: string; profile: ProfileUpdate }) =>
    runPending(async () => {
      const input = toZeroProfileUpdate(id, profile);
      const mutation = zero.mutate(appMutators.profiles.update(input));
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Profile update was rejected",
      );
      return toLegacyProfile({ ...input, name: input.name ?? "" });
    });
  return {
    mutate: (
      input: { id: string; profile: ProfileUpdate },
      options?: MutationOptions<Profile>,
    ) => {
      void run(input)
        .then((data) => {
          toast.success("Profile updated");
          options?.onSuccess?.(data);
        })
        .catch((err) =>
          toast.error("Failed to update profile: " + err.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
};

export const useDeleteProfile = () => {
  const zero = useZero() as ZeroMutateClient;
  const { isPending, runPending } = usePendingMutation();
  const run = (id: string) =>
    runPending(async () => {
      const mutation = zero.mutate(appMutators.profiles.delete({ id }));
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Profile delete was rejected",
      );
    });
  return {
    mutate: (id: string, options?: { onSuccess?: () => void }) => {
      void run(id)
        .then(() => {
          toast.success("Profile deleted");
          options?.onSuccess?.();
        })
        .catch((err) =>
          toast.error("Failed to delete profile: " + err.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
};
